/**
 * Fleet size decoupling (#208, M13 Q7).
 *
 * `fleet_size_range` is a CRM-only signal ("how many drivers do you have?")
 * and must NEVER feed pricing. The license quantity is a separate, explicit
 * owner choice ("how many licenses do you want to activate now?") — this
 * module only provides the suggested default bridging the two.
 */

export const FLEET_SIZE_RANGES = ['1-3', '4-10', '11-50', '50+'] as const;

export type FleetSizeRange = (typeof FLEET_SIZE_RANGES)[number];

export const isFleetSizeRange = (value: unknown): value is FleetSizeRange =>
  typeof value === 'string' && (FLEET_SIZE_RANGES as readonly string[]).includes(value);

/**
 * Suggested license quantity for the "activate now" screen, derived from the
 * declared fleet range. Deliberately conservative: the lower bound of each
 * range, capped at 20 for 50+ so we never pre-fill an aggressive commitment
 * (Q7: owners often activate far fewer licenses than their fleet size — the
 * canonical PT case is 50 drivers / 5 licenses). The owner adjusts freely.
 */
export const suggestedLicensesForRange = (range: FleetSizeRange | null | undefined): number => {
  switch (range) {
    case '1-3':
      return 1;
    case '4-10':
      return 4;
    case '11-50':
      return 11;
    case '50+':
      return 20;
    default:
      return 1;
  }
};
